// ForwardModal.js — Responsive: iOS, Android, Desktop, Tablet
import React from "react";
import { uuid } from "../utils/ChatUtils";

export default function ForwardModal({
  messages,
  users,
  groups,
  myChatId,
   mobileView,
  socket,
  cryptoFns,
  getUserKey,
  getGroupKey,
  onOptimisticInsert,
  onClose,
  onDone,
}) {
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState(new Set());
  const [sending, setSending] = React.useState(false);

  const allTargets = React.useMemo(() => {
   const dmTargets = (users || []).map((u) => ({
  id: `user-${u.chatId}`,
  type: "user",
  chatId: u.chatId,
  label: u.displayName || u.username || String(u.chatId),
  image: u.profileImageUrl || null,
  online: u.online ?? false,
}));
    const groupTargets = (groups || []).map((g) => ({
      id: `group-${g.groupId}`,
      type: "group",
      groupId: g.groupId,
      label: g.name,
      image: g.groupImageUrl || null,
    }));
    return [...dmTargets, ...groupTargets];
  }, [users, groups]);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return allTargets;
    const q = query.toLowerCase();
    return allTargets.filter((t) => t.label.toLowerCase().includes(q));
  }, [allTargets, query]);

  const selectedTargets = allTargets.filter((t) => selected.has(t.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const extractText = (msg) =>
    msg.text || msg.raw?.message?.conversation || msg.raw?.message?.text || "";

  const genId = () =>
    typeof uuid === "function"
      ? uuid()
      : Math.random().toString(36).slice(2) + Date.now();

  const buildOptimistic = (msgId, text, media, senderChatId, recipientChatId) => ({
    messageId: msgId,
    text,
    media: media || null,
    reply: null,
    timestamp: Date.now(),
    forwarded: true,
    raw: {
      key: { msgId, senderChatId, recipientChatId: recipientChatId || null },
      message: { conversation: text, media: media || null },
    },
  });

  const handleSend = async () => {
    if (!selected.size || sending) return;
    setSending(true);
    try {
      const targets = allTargets.filter((t) => selected.has(t.id));
      for (const target of targets) {
        for (const msg of messages) {
          const text = extractText(msg);
          const media = msg.media || msg.raw?.message?.media || null;
          const msgId = genId();

          if (target.type === "group") {
            socket.emit("group-send-message", { groupId: target.groupId, msgId, text, media: media || null, reply: null, forwarded: true });
            if (onOptimisticInsert && getGroupKey) {
              onOptimisticInsert(getGroupKey(target.groupId), buildOptimistic(msgId, text, media, myChatId, null));
            }
            continue;
          }

          let e2eePayload = null;
          let e2eeSenderPayload = null;
          if (cryptoFns && text) {
            try {
              const recipientPubKey = await cryptoFns.fetchPeerPublicKey(target.chatId);
              if (recipientPubKey) {
                const enc = await cryptoFns.encryptMessage(text, recipientPubKey);
                if (enc) {
                  e2eePayload = enc;
                  const senderPubKey = cryptoFns.getPeerPublicKey?.(myChatId) || (await cryptoFns.fetchPeerPublicKey(myChatId));
                  if (senderPubKey) {
                    const encSender = await cryptoFns.encryptMessage(text, senderPubKey);
                    if (encSender) {
                      e2eeSenderPayload = {
                        ciphertextSender: encSender.ciphertext,
                        ivSender: encSender.iv,
                        ephemeralPublicKeySender: encSender.ephemeralPublicKey,
                      };
                    }
                  }
                }
              }
            } catch (_e) {}
          }

          socket.emit("send-message", {
            recipientChatId: target.chatId,
            msgId,
            ...(e2eePayload || {}),
            ...(e2eeSenderPayload || {}),
            text: e2eePayload ? undefined : text,
            media: media || null,
            reply: null,
            forwarded: true,
          });

          if (onOptimisticInsert && getUserKey) {
            onOptimisticInsert(getUserKey(target.chatId), buildOptimistic(msgId, text, media, myChatId, target.chatId));
          }
        }
      }
      onDone();
    } catch (err) {
  
    } finally {
      setSending(false);
    }
  };

  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

   return (
    <div
      className={
        mobileView
          ? "fixed inset-0 z-50 flex flex-col bg-white dark:bg-neutral-900"
          : "fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      }
      onClick={mobileView ? undefined : onClose}
    >
      <div
        className={
          mobileView
            ? "flex flex-1 flex-col w-full h-full"
            : "flex flex-col w-full max-w-md h-[85vh] max-h-[640px] rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-neutral-900"
        }
        onClick={mobileView ? undefined : (e) => e.stopPropagation()}
      >

      {/* HEADER */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4 pb-3 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900"
        style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}
      >
        <button
          onClick={onClose}
          className="text-neutral-500 hover:text-red-500 text-2xl leading-none w-9 h-9 flex items-center justify-center touch-manipulation"
          aria-label="Close"
        >
          ←
        </button>
        <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100 flex-1">
          Forward message{messages.length > 1 ? "s" : ""}
        </span>
      </div>

      {/* SEARCH BAR */}
      <div className="flex-shrink-0 px-4 py-2 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 rounded-full px-4 py-2">
          <span className="text-neutral-400 text-sm">🔍</span>
          <input
            autoFocus
            className="flex-1 bg-transparent text-sm outline-none text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
            placeholder="Search contacts or groups…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query.length > 0 && (
            <button
              onClick={() => setQuery("")}
              className="text-neutral-400 hover:text-neutral-600 text-xs p-1 touch-manipulation"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* CONTACT LIST */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-neutral-400 text-sm gap-2">
            <span className="text-3xl">🔎</span>
            No contacts found
          </div>
        ) : (
          <ul>
            {filtered.map((target) => {
              const isChecked = selected.has(target.id);
              return (
                <li key={target.id}>
                  <button
                    className={`w-full flex items-center gap-3 px-4 py-3.5 sm:py-3 text-left transition-colors touch-manipulation ${
                      isChecked
                        ? "bg-green-50 dark:bg-green-900/20"
                        : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60 active:bg-neutral-100"
                    }`}
                    onClick={() => toggle(target.id)}
                  >
                    {/* Avatar */}
                    <div className="relative w-11 h-11 sm:w-12 sm:h-12 shrink-0">
                      {target.image ? (
                        <img src={target.image} alt={target.label} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <div className="w-full h-full rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xl">
                          {target.type === "group" ? "👥" : "👤"}
                        </div>
                      )}
                      {target.type === "user" && (
                        <span
                          className={`absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-neutral-900 ${
                            target.online ? "bg-green-500" : "bg-neutral-300"
                          }`}
                        />
                      )}
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate text-neutral-900 dark:text-neutral-100">
                        {target.label}
                      </p>
                      {target.type === "group" && (
                        <p className="text-xs text-neutral-400 mt-0.5">Group</p>
                      )}
                    </div>

                    {/* Tick */}
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                        isChecked
                          ? "bg-green-600 border-green-600 scale-110"
                          : "border-neutral-300 dark:border-neutral-600"
                      }`}
                    >
                      {isChecked && <span className="text-white text-xs font-bold leading-none">✓</span>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* FOOTER */}
      {selected.size > 0 && (
        <div
          className="flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3"
          style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}
        >
          {/* Selected chips */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide" style={{ WebkitOverflowScrolling: "touch" }}>
            {selectedTargets.map((t) => (
              <button
                key={t.id}
                onClick={() => toggle(t.id)}
                className="flex-shrink-0 flex items-center gap-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full pl-2 pr-3 py-1.5 sm:py-1 text-xs font-medium touch-manipulation"
              >
                <span className="text-base leading-none">{t.type === "group" ? "👥" : "👤"}</span>
                <span className="max-w-[80px] truncate">{t.label}</span>
                <span className="ml-0.5 text-green-600 dark:text-green-400">✕</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-500">
              {selected.size} recipient{selected.size > 1 ? "s" : ""} selected
            </span>
            <button
              disabled={sending}
              onClick={handleSend}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-colors touch-manipulation ${
                sending
                  ? "bg-neutral-300 dark:bg-neutral-700 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 active:bg-green-800"
              }`}
            >
              {sending ? "Sending…" : "Send ➤"}
            </button>
          </div>
              </div>
      )}
      </div>
    </div>
  );
}